import { useFacilityContext } from '@aspendental/shared-components-web';
import { DateTextInput, Form, SchemaBuilder, TextInput, useHookForm } from '@aspendental/shared-forms-web';
import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { format } from 'date-fns';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { type FieldValues } from 'react-hook-form';
import NeedsBasedQuestions from '@src/components/NeedsBasedQuestions';
import { REASON_FOR_VISIT_ID } from '@src/components/ReasonForVisit/ReasonForVisit.helpers';
import { DataLayerStepNames } from '@src/constants'; // FALLBACK_FACILITY_PHONE_NUMBER
import { useGeolocation } from '@src/hooks/useGeolocation';
import { useScrollToFirstError } from '@src/hooks/useScrollToFirstError';
import { useLanguagePreferenceTracking } from '@src/hooks/useLanguagePreferenceTracking';
import { DigitalVisitReasonEnum } from '@src/lib/__generated/api';
import { EventPatientInformationValueEventTypeEnum } from '@src/lib/__generated/user-events-api';
import GetCoordinates from '@src/services/coordinates';
import { useGetFacility } from '@src/services/useGetFacility';
import { userEvent } from '@src/services/userEvent';
import { PatientInformationSchema, PatientInformationSchemaES, useAppStore, useFormSectionStore, useGlobalUXStore, useGuarantorInformationFormStore, usePatientInformationFormStore, useReasonForVisitStore } from '@src/store';
import useNeedsBasedQuestionsStore from '@src/store/modules/needs-based-questions.store';
import { ageEligibleForProcedure, guarantorInformationNeeded } from '@src/utils/business-logic';
import { fireDataLayerEvent } from '@src/utils/data-layer-utils';
import { dateInvalid } from '@src/utils/date-parser';
import { trackFormValidationError } from '@src/utils/form-analytics';
import { unacceptableInputs } from '@src/utils/offensive-words';
import { sessionStorageHelper } from '@src/utils/session-storage-utils';
const PatientInformationForm = ({
  formId
}: {
  formId: string;
}) => {
  const [collectZipCode, setCollectZipCode] = useState(false);
  const [locationLookupError, setLocationLookupError] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [offensiveInputsFound, setOffensiveInputsFound] = useState(false);
  const nbqSectionRef = useRef<HTMLDivElement>(null);
  const [nbqScrollTrigger, setNbqScrollTrigger] = useState(0);
  const router = useRouter();
  const {
    locale
  } = router;
  const openDialogWithError = useGlobalUXStore(store => store.openDialogWithError);
  const firstName = usePatientInformationFormStore(store => store.firstName);
  const lastName = usePatientInformationFormStore(store => store.lastName);
  const dateOfBirth = usePatientInformationFormStore(store => store.dateOfBirth);
  const zipCode = usePatientInformationFormStore(store => store.zipCode);
  const geoLocation = usePatientInformationFormStore(store => store.geoLocation);
  const patientAge = usePatientInformationFormStore(store => store.patientAge);
  const setFirstName = usePatientInformationFormStore(store => store.setFirstName);
  const setLastName = usePatientInformationFormStore(store => store.setLastName);
  const setDateOfBirth = usePatientInformationFormStore(store => store.setDateOfBirth);
  const setZipCode = usePatientInformationFormStore(store => store.setZipCode);
  const setGeolocation = usePatientInformationFormStore(store => store.setGeolocation);
  const completeActiveNode = useFormSectionStore(store => store.completeActiveNode);
  const {
    t
  } = useTranslation('patientInformationForm');
  const setGuarantorRequired = useGuarantorInformationFormStore(store => store.setRequired);
  const setGuarantorInfoNeeded = useFormSectionStore(store => store.setGuarantorInfoNeeded);
  const {
    reasonForVisit
  } = useReasonForVisitStore(store => store);
  const webFacilityDetails = useFacilityContext();
  const {
    hasNeedsBasedQuestions,
    needsBasedAnswers,
    setNeedsBasedAnswers
  } = useNeedsBasedQuestionsStore(store => store);
  const {
    mottoFlow
  } = useAppStore(store => store);
  const geolocationService = useGeolocation();
  const facilityCode = typeof router.query.fc === 'string' ? router.query.fc : '';
  const hasFacilityCode = Boolean(facilityCode);
  const {
    isLoading: isFacilityLoading,
    data: facilityData
  } = useGetFacility({
    facilityCode
  });
  const facilityHasCoordinates = typeof facilityData?.latitude === 'number' && typeof facilityData?.longitude === 'number';
  sessionStorageHelper.setKey(DataLayerStepNames.patientInformation, 'false');
  useEffect(() => {
    if (geoLocation.latitude && geoLocation.longitude) {
      setCollectZipCode(false);
      return;
    }
    if (geolocationService.loading) {
      return;
    }
    if (geolocationService.error) {
      // If a facility deep link is still resolving, keep the zipcode field
      // hidden until the facility request settles.
      if (hasFacilityCode && isFacilityLoading) {
        return;
      }
      // If the facility deep link resolved with usable coordinates, zip code is not needed.
      if (hasFacilityCode && facilityHasCoordinates) {
        return;
      }
      return setCollectZipCode(true);
    }
    const {
      latitude,
      longitude
    } = geolocationService;
    if (geolocationService.loading && typeof latitude !== 'number' || typeof longitude !== 'number') {
      return setCollectZipCode(true);
    }
    setGeolocation(latitude as number, longitude as number);
  }, [geolocationService, setGeolocation, geoLocation, hasFacilityCode, isFacilityLoading, facilityHasCoordinates]);
  useEffect(() => {
    //this method is added to prevent multiple data layer events from being fired in mobile view
    //pass the step name as first parameter
    if (webFacilityDetails?.officeInfo?.code && webFacilityDetails?.officeInfo?.name) {
      fireDataLayerEvent(DataLayerStepNames.patientInformation, {
        language_preference: locale,
        brand: mottoFlow ? 'mottoaligners' : 'aspendental',
        step_name: DataLayerStepNames.patientInformation,
        reason_for_visit: reasonForVisit,
        reason_for_visit_id: REASON_FOR_VISIT_ID[reasonForVisit],
        website_selected_facility_id: webFacilityDetails?.officeInfo?.code,
        website_selected_facility_name: webFacilityDetails?.officeInfo?.name
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webFacilityDetails?.officeInfo?.code, webFacilityDetails?.officeInfo?.name]);
  useLanguagePreferenceTracking({
    stepName: DataLayerStepNames.patientInformation,
    webFacilityDetails,
    brand: mottoFlow ? 'mottoaligners' : 'aspendental'
  });

  // age is determined from the entries in this form, so as soon as age
  // is calculated we set the requirement for guarantor information
  const determineGuarantorInfoNeeded = () => {
    const age = patientAge();
    if (age) {
      const guarantorNeeded = guarantorInformationNeeded(age);
      setGuarantorRequired(guarantorNeeded);
      setGuarantorInfoNeeded(guarantorNeeded);
    } else {
      console.error('patientAge is invalid, cannot determine if gurantor information is neeeded.');
    }
  };
  const runLocationSearch = async ({
    zipCode,
    lastName,
    firstName,
    dateOfBirth
  }) => {
    let lookupFailed = false;
    try {
      const coords = await GetCoordinates(zipCode);
      if (typeof coords.lat === 'number' && typeof coords.lng === 'number') {
        setGeolocation(coords.lat, coords.lng);
        completeActiveNode();
        logUserEvent({
          lastName,
          firstName,
          dateOfBirth
        });
      } else {
        lookupFailed = true;
      }
    } catch (e) {
      console.error(e);
      lookupFailed = true;
    } finally {
      if (lookupFailed) {
        setLocationLookupError(true);
        trackFormValidationError({
          formName: 'patient_information',
          errorType: 'location_lookup_failed'
        });
      }
    }
  };
  const logUserEvent = ({
    firstName,
    lastName,
    dateOfBirth
  }) => {
    userEvent({
      event_type: EventPatientInformationValueEventTypeEnum.PatientInfo,
      event_data: {
        first_name: firstName,
        last_name: lastName,
        date_of_birth: new Intl.DateTimeFormat('default', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        }).format(dateOfBirth ?? undefined)
      }
    });
  };
  function isNBQFormValid(data: Record<string, string[]> | undefined) {
    for (const value of Object.values(data ?? {})) {
      if (value.length === 0) return false;
    }
    return true;
  }
  const handleSubmit = async (fields: FieldValues) => {
    setLocationLookupError(false);
    if (hasNeedsBasedQuestions && !isNBQFormValid(needsBasedAnswers)) {
      setHasError(true);
      setNbqScrollTrigger(n => n + 1);
      trackFormValidationError({
        formName: 'patient_information',
        errorType: 'nbq_incomplete'
      });
      return;
    } else {
      setHasError(false);
    }
    const {
      firstName,
      lastName,
      dateOfBirth
    } = fields;
    const unacceptableFields = unacceptableInputs([{
      name: 'firstName',
      value: firstName
    }, {
      name: 'lastName',
      value: lastName
    }]);
    if (unacceptableFields.length) {
      console.error('offensive inputs detected: ', unacceptableFields.join(', '));
      setOffensiveInputsFound(true);
      trackFormValidationError({
        formName: 'patient_information',
        errorType: 'offensive_input',
        fields: unacceptableFields
      });
      return;
    } else {
      setOffensiveInputsFound(false);
    }
    setFirstName(firstName);
    setLastName(lastName);
    setDateOfBirth(dateOfBirth);
    if (fields.zipCode) {
      setZipCode(fields.zipCode);
    }
    determineGuarantorInfoNeeded();
    const useCachedGeolocation = fields.zipCode === zipCode || fields.zipCode === '';
    const age = patientAge();
    const ageOK = ageEligibleForProcedure(age || 0, reasonForVisit as DigitalVisitReasonEnum);
    if (ageOK) {
      useCachedGeolocation ? completeForm({
        lastName,
        firstName,
        dateOfBirth
      }) : runLocationSearch({
        zipCode: fields.zipCode,
        lastName,
        firstName,
        dateOfBirth
      });
    } else {
      openDialogWithError(t('onlineUnavailable'));
      trackFormValidationError({
        formName: 'patient_information',
        errorType: 'age_ineligible'
      });
    }
  };
  const completeForm = ({
    lastName,
    firstName,
    dateOfBirth
  }) => {
    completeActiveNode();
    logUserEvent({
      lastName,
      firstName,
      dateOfBirth
    });
  };
  const englishSchema = locale === 'en-US';
  const schema = SchemaBuilder([{
    name: 'firstName',
    validation: englishSchema ? PatientInformationSchema.firstName : PatientInformationSchemaES.firstName
  }, {
    name: 'lastName',
    validation: englishSchema ? PatientInformationSchema.lastName : PatientInformationSchemaES.lastName
  }, {
    name: 'dateOfBirth',
    validation: englishSchema ? PatientInformationSchema.dateOfBirth : PatientInformationSchemaES.dateOfBirth
  }, {
    name: 'zipCode',
    validation: collectZipCode ? PatientInformationSchema.zipCode : PatientInformationSchema.zipCodeOptional
  }]);
  const initialValueDateOfBirth = dateInvalid(dateOfBirth) ? '' : format(dateOfBirth as Date, 'MM/dd/yyyy');
  const defaultValues = {
    firstName,
    lastName,
    dateOfBirth: initialValueDateOfBirth,
    zipCode,
    geoLocation
  };
  const form = useHookForm({
    schema,
    defaultValues,
    shouldFocusError: false
  });
  useScrollToFirstError(form as any, formId);
  useEffect(() => {
    if (form.formState.submitCount === 0) return;
    const errors = form.formState.errors;
    const failedFields = Object.keys(errors);
    if (failedFields.length === 0) return;
    trackFormValidationError({
      formName: 'patient_information',
      errorType: 'field_validation',
      fields: failedFields,
      fieldMessages: Object.fromEntries(failedFields.map(field => {
        const errorField = errors[field as keyof typeof errors];
        const message = typeof errorField?.message === 'string' ? errorField.message : 'unknown';
        return [field, message];
      }))
    });
    // intentionally omit errors from deps — only track once per submit attempt, not on every keystroke
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.formState.submitCount]);
  useEffect(() => {
    if (nbqScrollTrigger === 0) return;
    nbqSectionRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
    nbqSectionRef.current?.focus({
      preventScroll: true
    });
  }, [nbqScrollTrigger]);
  return <Form form={form as any} onSubmit={handleSubmit} data-test-id="patient-information-form" formId={formId} data-sentry-element="Form" data-sentry-component="PatientInformationForm" data-sentry-source-file="PatientInformationForm.tsx">
			<Grid container spacing={2} data-sentry-element="Grid" data-sentry-source-file="PatientInformationForm.tsx">
				<Grid item xs={12} sm={6} data-private data-sentry-element="Grid" data-sentry-source-file="PatientInformationForm.tsx">
					<TextInput name="firstName" label={t('firstNameLabel')} data-test-id="patient-info-firstname" data-sentry-element="TextInput" data-sentry-source-file="PatientInformationForm.tsx" />
				</Grid>
				<Grid item xs={12} sm={6} data-private data-sentry-element="Grid" data-sentry-source-file="PatientInformationForm.tsx">
					<TextInput name="lastName" label={t('lastNameLabel')} data-sentry-element="TextInput" data-sentry-source-file="PatientInformationForm.tsx" />
				</Grid>
				<Grid item xs={12} sm={6} data-private data-sentry-element="Grid" data-sentry-source-file="PatientInformationForm.tsx">
					<DateTextInput label={t('dobLabel')} name="dateOfBirth" autoComplete="bday" required data-sentry-element="DateTextInput" data-sentry-source-file="PatientInformationForm.tsx" />
				</Grid>
				{collectZipCode && <Grid item sm={12} md={6} data-private>
						<TextInput name="zipCode" label={t('zipCodeLabel')} maxLength={5} />
					</Grid>}
				{offensiveInputsFound && <Grid item xs={12}>
						<Alert severity="error" variant="flatError">
							<Typography color="text.secondary">{t('offensiveInputWarning')}</Typography>
						</Alert>
					</Grid>}
				{locationLookupError && <Grid item xs={12}>
						<Alert severity="error" variant="flatError">
							<Typography color="text.secondary">{t('zipCodeError')}</Typography>
						</Alert>
					</Grid>}
				{hasNeedsBasedQuestions ? <div ref={nbqSectionRef} tabIndex={-1}>
						<NeedsBasedQuestions setNeedsBasedAnswers={setNeedsBasedAnswers} needsBasedAnswers={needsBasedAnswers} hasError={hasError} />
					</div> : null}
			</Grid>
		</Form>;
};
export default PatientInformationForm;